import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  getTeamDetails,
  updateTeam,
  deleteTeam,
  getClubCoaches,
  getClubTeams,
  getClubSports,
  getAgeGroups,
  setTeamCoaches,
  type TeamDetails as TeamDetailsType,
  type ClubCoach,
  type ClubSport,
  type AgeGroup,
} from "../../services/api";
import { usePageTitle } from "../../hooks/usePageTitle";
import { useAuth } from "../../context/AuthContext";
import ClubAdminLayout from "../../layouts/ClubAdminLayout";
import Team360 from "../../components/club/Team360";
import TeamProducts from "../../components/club/TeamProducts";
import { SectionCard } from "../../components/admin/AdminCard";
import toast from "react-hot-toast";

type TeamListItem = { id: string; name: string; age_group?: string | null; sport_type?: string | null };

export default function TeamDetails() {
  const { t } = useTranslation();
  const { teamId } = useParams<{ teamId: string }>();
  const navigate = useNavigate();
  // Club-admin viewers need the ClubAdminLayout sidebar back. Platform admin
  // gets a sidebar from App.tsx, so render bare for them.
  //
  // NOTE: do NOT extract this into a `PageWrapper` component defined inside
  // TeamDetails — that creates a new component type on every render, which
  // React treats as a remount and re-runs ClubAdminLayout's
  // "close menu on pathname change" effect on EVERY parent re-render. That
  // bug caused the mobile hamburger to be visually toggled and then
  // immediately reset, so the menu never appeared.
  const { activeRole, activeClubId } = useAuth();
  const wrap = activeRole === "club_admin";
  const layoutShell = (children: React.ReactNode) =>
    wrap ? <ClubAdminLayout>{children}</ClubAdminLayout> : <>{children}</>;

  const [team, setTeam] = useState<TeamDetailsType | null>(null);
  const [allTeams, setAllTeams] = useState<TeamListItem[]>([]);
  const [coaches, setCoaches] = useState<ClubCoach[]>([]);
  const [sports, setSports] = useState<ClubSport[]>([]);
  const [ageGroups, setAgeGroups] = useState<AgeGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState({ name: "", age_group_id: "", sport_id: "" });
  /** The whole coaching staff, head coach first. */
  const [editCoachIds, setEditCoachIds] = useState<string[]>([]);
  const [editHeadId, setEditHeadId] = useState<string>("");
  /** Bumped after a save so the 360 view refetches instead of showing stale figures. */
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    getAgeGroups()
      .then(setAgeGroups)
      .catch(() => setAgeGroups([]));
  }, []);

  // Every team of the owning club, for the team selector in the header. Reads
  // the team's own club now that the payload carries it, so a platform admin
  // gets the selector too instead of an empty list.
  useEffect(() => {
    const clubId = team?.club_id || activeClubId;
    if (!clubId) return;
    getClubTeams(clubId)
      .then(setAllTeams)
      .catch(() => setAllTeams([]));
  }, [team?.club_id, activeClubId]);

  usePageTitle(team?.name || t("team.details", "Detalii echipă"));

  useEffect(() => {
    if (!teamId) return;
    loadTeam();
  }, [teamId]);

  async function loadTeam() {
    if (!teamId) return;
    setLoading(true);
    try {
      const data = await getTeamDetails(teamId);
      setTeam(data);
      resetEditForm(data);
    } catch (error) {
      console.error("Failed to load team:", error);
    } finally {
      setLoading(false);
    }
  }

  /** Put the form back to whatever the team currently is. */
  function resetEditForm(data: TeamDetailsType) {
    setEditForm({
      name: data.name,
      age_group_id: data.age_group_id || "",
      sport_id: data.sport_id || "",
    });
    const staff = (data.coaches || []).map((c) => c.id);
    setEditCoachIds(data.coach_id && !staff.includes(data.coach_id) ? [data.coach_id, ...staff] : staff);
    setEditHeadId(data.coach_id || "");
  }

  /** The club that owns the team — not the viewer's, so this works for a platform admin. */
  const ownerClubId = team?.club_id || activeClubId || null;

  async function openEdit() {
    if (team) resetEditForm(team);
    setEditMode(true);
    if (!ownerClubId) return;
    // Loaded on demand: without this the coach list and the sport dropdown
    // come up empty and the already-assigned ones cannot be preselected.
    const [coachList, sportList] = await Promise.all([
      getClubCoaches(ownerClubId).catch(() => [] as ClubCoach[]),
      getClubSports(ownerClubId).catch(() => [] as ClubSport[]),
    ]);
    setCoaches(coachList);
    setSports(sportList);
  }

  const toggleCoach = (id: string) =>
    setEditCoachIds((prev) => {
      if (prev.includes(id)) {
        const next = prev.filter((c) => c !== id);
        // Taking the head coach off the team leaves nobody in charge, so the
        // next on the list steps up rather than the field going stale.
        if (editHeadId === id) setEditHeadId(next[0] || "");
        return next;
      }
      if (!editHeadId) setEditHeadId(id);
      return [...prev, id];
    });

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    if (!teamId || !editForm.name.trim()) return;
    setSaving(true);
    try {
      await updateTeam(teamId, {
        name: editForm.name.trim(),
        age_group_id: editForm.age_group_id || null,
        // "" clears the sport; the server refuses a sport the club does not
        // practise rather than storing a name that disagrees with the id.
        sport_id: editForm.sport_id || null,
      });
      // The staff list is its own call: it has to be able to REMOVE a coach,
      // which the team patch (one coach_id) cannot express.
      await setTeamCoaches(teamId, editCoachIds, editHeadId || null);
      await loadTeam();
      setReloadKey((k) => k + 1);
      setEditMode(false);
      toast.success(t("common.saved", "Salvat cu succes!"));
    } catch (error: any) {
      console.error("Failed to update team:", error);
      toast.error(error?.response?.data?.error || "Actualizarea echipei a eșuat");
    } finally {
      setSaving(false);
    }
  }

  // Inline delete-confirmation state. Using an in-page panel avoids the
  // browser confirm() dialog which can be suppressed by ad-blockers /
  // privacy extensions, causing the delete button to look broken.
  const [pendingDelete, setPendingDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  async function performDelete() {
    if (!teamId) return;
    setDeleting(true);
    try {
      await deleteTeam(teamId);
      navigate("/dashboard/admin-club");
    } catch (error) {
      console.error("Failed to delete team:", error);
      alert("Ștergerea echipei a eșuat");
      setDeleting(false);
      setPendingDelete(false);
    }
  }

  if (loading) {
    return layoutShell(
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!team) {
    return layoutShell(
      <div className="p-3 sm:p-6">
        <div className="text-center py-12">
          <h2 className="text-xl font-semibold text-slate-800">{t("team.notFound", "Echipa nu a fost găsită")}</h2>
          <button
            onClick={() => navigate("/dashboard/admin-club")}
            className="mt-4 text-indigo-600 hover:underline"
          >
            {t("common.goBack", "Înapoi")}
          </button>
        </div>
      </div>
    );
  }

  return layoutShell(
    <div className={wrap ? "" : "min-h-screen bg-slate-50 p-3 sm:p-6"}>
      <div>
        <div className="min-w-0 space-y-4 sm:space-y-6">

        {/* Header — title row stacks vertically on mobile so the edit/delete
            actions don't squeeze into a tiny strip next to a long team name. */}
        <div className="mb-4 sm:mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-center gap-3 sm:gap-4 min-w-0">
              <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-xl sm:rounded-2xl bg-indigo-100 flex items-center justify-center text-indigo-600 font-bold text-lg sm:text-2xl flex-shrink-0">
                {team.name.substring(0, 2).toUpperCase()}
              </div>
              <div className="min-w-0">
                <h1 className="text-xl sm:text-2xl font-bold text-slate-800 break-words">{team.name}</h1>
                <div className="flex flex-wrap items-center gap-1.5 mt-1">
                  {team.age_group && (
                    <span className="px-2 py-0.5 rounded-full text-xs bg-blue-100 text-blue-700">{team.age_group}</span>
                  )}
                  {team.sport_type && (
                    <span className="px-2 py-0.5 rounded-full text-xs bg-emerald-100 text-emerald-700">{team.sport_type}</span>
                  )}
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              {/* Switching team changes the page. This replaced a fixed list
                  down the left-hand side: one control, and the whole width
                  left for the team itself. */}
              {!editMode && allTeams.length > 1 && (
                <select
                  value={teamId || ""}
                  onChange={(e) => navigate(`/dashboard/admin-club/teams/${e.target.value}`)}
                  className="px-3 py-2 text-sm bg-white border rounded-xl border-slate-300 text-slate-700 max-w-[220px] focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  aria-label={t("admin.teams", "Echipe")}
                >
                  {allTeams.map((tm) => (
                    <option key={tm.id} value={tm.id}>
                      {tm.name}
                      {tm.age_group ? ` · ${tm.age_group}` : ""}
                    </option>
                  ))}
                </select>
              )}
              {!editMode ? (
                <>
                  <button
                    onClick={openEdit}
                    className="px-3 sm:px-4 py-2 text-sm sm:text-base bg-indigo-500 hover:bg-indigo-600 text-white rounded-xl font-medium transition-colors"
                  >
                    {t("common.edit", "Editează")}
                  </button>
                  {pendingDelete ? (
                    <div className="flex items-center gap-2 px-3 py-1.5 bg-red-50 border border-red-200 rounded-xl">
                      <span className="text-sm text-red-800 font-medium">
                        {t("admin.confirmDeleteTeam", "Ștergi echipa? Acțiunea nu poate fi anulată.")}
                      </span>
                      <button
                        onClick={performDelete}
                        disabled={deleting}
                        className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white rounded text-xs font-medium disabled:opacity-50"
                      >
                        {deleting ? "..." : t("common.yes", "Da")}
                      </button>
                      <button
                        onClick={() => setPendingDelete(false)}
                        disabled={deleting}
                        className="px-3 py-1 bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 rounded text-xs font-medium disabled:opacity-50"
                      >
                        {t("common.no", "Nu")}
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setPendingDelete(true)}
                      disabled={saving}
                      className="px-3 sm:px-4 py-2 text-sm sm:text-base text-rose-600 hover:bg-rose-50 rounded-xl font-medium transition-colors"
                    >
                      {t("common.delete", "Șterge")}
                    </button>
                  )}
                </>
              ) : (
                <>
                  <button
                    onClick={() => {
                      if (team) resetEditForm(team);
                      setEditMode(false);
                    }}
                    className="px-3 sm:px-4 py-2 text-sm sm:text-base bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl font-medium transition-colors"
                  >
                    {t("common.cancel", "Anulează")}
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={saving || !editForm.name.trim()}
                    className="px-3 sm:px-4 py-2 text-sm sm:text-base bg-indigo-500 hover:bg-indigo-600 text-white rounded-xl font-medium transition-colors disabled:opacity-50 flex items-center gap-2"
                  >
                    {saving && <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>}
                    {t("common.save", "Salvează")}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Editing the team. Deliberately narrow: the four things that are
            properties OF the team — its name, its sport, its age group, who
            coaches it and what it is charged for. Everything else about a team
            (its squad, its trainings, its figures) is edited where it lives,
            and a form that tried to hold all of it would hold none of it well.

            This panel replaced an "Editează" button that set a flag nothing
            rendered, so the button did nothing at all. */}
        {editMode && (
          <div className="space-y-4 sm:space-y-6">
            <SectionCard title={t("team.editTeam", "Detaliile echipei")} bodyPadded>
              <form onSubmit={handleSave} className="space-y-4">
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <label className="block mb-1 text-xs font-semibold text-slate-600" htmlFor="td-name">
                      {t("team.name", "Numele echipei")}
                    </label>
                    <input
                      id="td-name"
                      value={editForm.name}
                      onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))}
                      className="w-full px-3 py-2 text-sm bg-white border rounded-lg border-slate-300 text-slate-800"
                    />
                    {!editForm.name.trim() && (
                      <p className="mt-1 text-xs text-rose-600">
                        {t("team.nameRequired", "Echipa are nevoie de un nume.")}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block mb-1 text-xs font-semibold text-slate-600" htmlFor="td-sport">
                      {t("team.sport", "Sportul echipei")}
                    </label>
                    <select
                      id="td-sport"
                      value={editForm.sport_id}
                      onChange={(e) => setEditForm((f) => ({ ...f, sport_id: e.target.value }))}
                      className="w-full px-3 py-2 text-sm bg-white border rounded-lg border-slate-300 text-slate-800"
                    >
                      <option value="">{t("team.noSport", "Fără sport")}</option>
                      {sports.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.name}
                        </option>
                      ))}
                      {/* The team's current sport, when the club no longer
                          lists it — otherwise opening the form would silently
                          offer to clear it. */}
                      {team.sport_id && !sports.some((s) => s.id === team.sport_id) && (
                        <option value={team.sport_id}>{team.sport_type} (nu mai e în club)</option>
                      )}
                    </select>
                    {sports.length === 0 && (
                      <p className="mt-1 text-xs text-slate-400">
                        {t("team.noClubSports", "Clubul nu are sporturi configurate.")}
                      </p>
                    )}
                  </div>
                </div>

                <div className="sm:w-1/2">
                  <label className="block mb-1 text-xs font-semibold text-slate-600" htmlFor="td-age">
                    {t("team.ageGroup", "Categoria de vârstă")}
                  </label>
                  <select
                    id="td-age"
                    value={editForm.age_group_id}
                    onChange={(e) => setEditForm((f) => ({ ...f, age_group_id: e.target.value }))}
                    className="w-full px-3 py-2 text-sm bg-white border rounded-lg border-slate-300 text-slate-800"
                  >
                    <option value="">{t("team.noAgeGroup", "Fără categorie")}</option>
                    {ageGroups.map((g) => (
                      <option key={g.id} value={g.id}>
                        {g.name}
                        {g.min_age != null || g.max_age != null
                          ? ` (${g.min_age ?? "?"}–${g.max_age ?? "?"})`
                          : ""}
                      </option>
                    ))}
                  </select>
                </div>

                {/* The coaching staff. A list with ticks rather than one
                    dropdown, because a team often has two or three people on
                    it and the old single `coach_id` could not say so. */}
                <div className="pt-4 border-t border-slate-100">
                  <p className="mb-1 text-xs font-bold tracking-wide uppercase text-slate-400">
                    {t("team.coaches", "Antrenori alocați")}
                  </p>
                  {coaches.length === 0 ? (
                    <p className="text-sm text-slate-500">
                      {t("team.noClubCoaches", "Clubul nu are antrenori. Îi inviți din pagina Antrenori.")}
                    </p>
                  ) : (
                    <div className="mt-2 divide-y divide-slate-100">
                      {coaches.map((c) => {
                        const picked = editCoachIds.includes(c.user.id);
                        return (
                          <div key={c.user.id} className="flex flex-wrap items-center gap-3 py-2">
                            <label className="flex items-center flex-1 min-w-[200px] gap-3 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={picked}
                                onChange={() => toggleCoach(c.user.id)}
                                className="w-4 h-4 rounded text-indigo-600 border-slate-300 focus:ring-indigo-500"
                              />
                              <span className="min-w-0">
                                <span className="block text-sm font-medium truncate text-slate-800">
                                  {c.user.full_name || c.user.email}
                                </span>
                                <span className="block text-xs truncate text-slate-500">
                                  {(c.sports || []).map((s) => s.name).join(", ") || c.user.email}
                                </span>
                              </span>
                            </label>
                            {picked && (
                              <label className="flex items-center gap-1.5 text-xs font-semibold cursor-pointer text-slate-600">
                                <input
                                  type="radio"
                                  name="td-head-coach"
                                  checked={editHeadId === c.user.id}
                                  onChange={() => setEditHeadId(c.user.id)}
                                  className="w-3.5 h-3.5 text-indigo-600 border-slate-300 focus:ring-indigo-500"
                                />
                                {t("team.headCoach", "principal")}
                              </label>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                  <p className="mt-2 text-[11px] text-slate-400">
                    {t(
                      "team.coachRemovalNote",
                      "Debifarea scoate antrenorul de pe echipă. Contul, clubul și celelalte echipe ale lui rămân neatinse."
                    )}
                  </p>
                </div>

                <div className="flex items-center justify-end gap-2 pt-4 border-t border-slate-100">
                  <button
                    type="button"
                    onClick={() => {
                      if (team) resetEditForm(team);
                      setEditMode(false);
                    }}
                    className="px-4 py-2 text-sm font-semibold rounded-lg bg-slate-100 text-slate-700 hover:bg-slate-200"
                  >
                    {t("common.cancel", "Anulează")}
                  </button>
                  <button
                    type="submit"
                    disabled={saving || !editForm.name.trim()}
                    className="px-4 py-2 text-sm font-bold text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                  >
                    {saving ? t("common.saving", "Se salvează...") : t("common.save", "Salvează")}
                  </button>
                </div>
              </form>
            </SectionCard>

            {/* Services save themselves as you add or remove them, so they sit
                outside the form: a fee attached to a team charges the whole
                roster immediately, and burying that behind a "Salvează" that
                also renames the team would make it easy to do by accident. */}
            {teamId && (
              <SectionCard title={t("team.services", "Servicii și taxe")} bodyPadded>
                <TeamProducts teamId={teamId} teamName={team.name} />
              </SectionCard>
            )}
          </div>
        )}

        {/* The 360 view. The stat tiles, the coach card and the plain athlete
            list that used to sit here are all inside it now, with the figures
            a club actually asks about: turnout, fees, upgrades, affiliate
            earnings and the internal ranking. */}
        {teamId && !editMode && <Team360 key={reloadKey} teamId={teamId} />}
        </div>
      </div>
    </div>
  );
}
